import { Head, useForm } from "@inertiajs/react"
import { AppLayout } from "@/layouts/app-layout"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import InputError from "@/components/input-error"
import { ArrowLeft } from "lucide-react"
import { portfolioPath } from "@/routes"
interface Portfolio {
  id: number
  name: string
}

interface PositionFormData {
  portfolio_position: {
    symbol: string
    shares: string
    average_cost: string
  }
}

interface PortfolioPositionsNewProps {
  portfolio: Portfolio
  errors?: string[]
}

export default function PortfolioPositionsNew({
  portfolio,
  errors = [],
}: PortfolioPositionsNewProps) {
  const { data, setData, post, processing } = useForm<PositionFormData>({
    portfolio_position: {
      symbol: "",
      shares: "",
      average_cost: "",
    },
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/portfolios/${portfolio.id}/portfolio_positions`)
  }

  return (
    <AppLayout>
      <Head title={`Add Position - ${portfolio.name}`} />

      <div className="mx-auto max-w-2xl space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <a href={portfolioPath(portfolio.id)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Portfolio
            </a>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Add Position to {portfolio.name}
            </h1>
            <p className="text-muted-foreground">
              Add a new stock position to track in your portfolio
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Position Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="symbol">Stock Symbol *</Label>
                <Input
                  id="symbol"
                  type="text"
                  value={data.portfolio_position.symbol}
                  onChange={(e) =>
                    setData("portfolio_position", {
                      ...data.portfolio_position,
                      symbol: e.target.value.toUpperCase(),
                    })
                  }
                  placeholder="e.g., AAPL, MSFT, TSLA"
                  required
                />
                <InputError
                  message={errors.find((e) => e.includes("symbol"))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="shares">Number of Shares *</Label>
                <Input
                  id="shares"
                  type="number"
                  min="0"
                  step="0.01"
                  value={data.portfolio_position.shares}
                  onChange={(e) =>
                    setData("portfolio_position", {
                      ...data.portfolio_position,
                      shares: e.target.value,
                    })
                  }
                  placeholder="e.g., 100"
                  required
                />
                <InputError
                  message={errors.find((e) => e.includes("shares"))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="average_cost">Average Cost per Share *</Label>
                <div className="relative">
                  <span className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2">
                    $
                  </span>
                  <Input
                    id="average_cost"
                    type="number"
                    min="0"
                    step="0.01"
                    value={data.portfolio_position.average_cost}
                    onChange={(e) =>
                      setData("portfolio_position", {
                        ...data.portfolio_position,
                        average_cost: e.target.value,
                      })
                    }
                    className="pl-8"
                    placeholder="e.g., 150.00"
                    required
                  />
                </div>
                <InputError
                  message={errors.find((e) => e.includes("average_cost"))}
                />
              </div>

              {/* Error Messages */}
              {errors.length > 0 && (
                <div className="bg-destructive/15 rounded-md p-4">
                  <div className="flex">
                    <div className="ml-3">
                      <h3 className="text-destructive text-sm font-medium">
                        There were errors with your submission:
                      </h3>
                      <div className="text-destructive mt-2 text-sm">
                        <ul role="list" className="list-disc space-y-1 pl-5">
                          {errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <Button type="submit" disabled={processing}>
                  {processing ? "Adding..." : "Add Position"}
                </Button>
                <Button type="button" variant="outline" asChild>
                  <a href={portfolioPath(portfolio.id)}>Cancel</a>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Tips */}
        <Card>
          <CardHeader>
            <CardTitle>Tips for Adding Positions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-muted-foreground space-y-2 text-sm">
              <p>
                💡 <strong>Stock Symbol:</strong> Use the official ticker symbol
                (e.g., AAPL for Apple Inc.)
              </p>
              <p>
                📊 <strong>Shares:</strong> Enter the total number of shares you
                own of this stock
              </p>
              <p>
                💰 <strong>Average Cost:</strong> This is your average purchase
                price per share, including commissions
              </p>
              <p>
                🔄 <strong>Automatic Updates:</strong> Current prices will be
                fetched automatically from market data
              </p>
              <p>
                📈 <strong>Analytics:</strong> Your portfolio analytics will
                update automatically after adding positions
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
